/**
 * Compositional unfolding for essential oils & absolutes.
 *
 * SOTA fragrance regulatory platforms (Coptis, Formpak, dataEssence, Selerant
 * DevEX) treat each natural raw material as a *mixture* of chemical
 * constituents rather than a single ingredient row. When the formulator
 * declares "5% Bergamot oil expressed", the platform decomposes that into
 *
 *   5% × 38%  limonene        = 1.9%   limonene contribution
 *   5% × 30%  linalyl acetate = 1.5%   linalyl acetate contribution
 *   5% × 0.3% bergapten       = 0.015% bergapten contribution
 *   …
 *
 * and runs IFRA + EU Annex limits against each *constituent* contribution.
 * It also aggregates allergen content across every oil in the formula —
 * three different oils each carrying 15% linalool sum to a single
 * total-linalool figure that is what the EU allergen threshold actually
 * applies to.
 *
 * Composition data lives in `src/data/essential_oil_compositions.json` and
 * is keyed by the same `<name>.pdf` keys the IFRA dataset uses, so the
 * unfolded constituent rows round-trip back into the existing fuzzy-search
 * + threshold-check pipeline.
 */

import compositions from "../data/essential_oil_compositions.json";

const normaliseName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const normaliseCas = (s) => {
  const m = String(s || "").match(/\d{2,7}-\d{2}-\d/);
  return m ? m[0] : "";
};

// Build a lookup keyed by CAS + by INCI alias so we can resolve an oil row
// without depending on exact name matches against the JSON keys.
const buildCompositionIndex = () => {
  const byCas = new Map();
  const byAlias = new Map();
  Object.entries(compositions).forEach(([key, entry]) => {
    if (key.startsWith("_")) return;
    (entry.casNumbers || []).forEach((cas) => {
      byCas.set(normaliseCas(cas), { key, entry });
    });
    (entry.inciAliases || []).forEach((alias) => {
      byAlias.set(normaliseName(alias), { key, entry });
    });
    byAlias.set(normaliseName(key.replace(/\.pdf$/, "")), { key, entry });
  });
  return { byCas, byAlias };
};

const COMP_INDEX = buildCompositionIndex();

// Resolve the composition profile for a single ingredient row. Tries CAS
// first (most reliable), then any normalised alias. Returns `null` if the
// ingredient isn't a known essential oil.
export const resolveComposition = (ingredient) => {
  const cas = normaliseCas(ingredient.cas_number || ingredient.CAS_NUMBER);
  if (cas) {
    const hit = COMP_INDEX.byCas.get(cas);
    if (hit) return hit;
  }
  const name = normaliseName(
    ingredient.ingredient_name ||
      ingredient.chemical_name ||
      ingredient.name ||
      ingredient.MATERIAL ||
      ""
  );
  if (name) {
    const hit = COMP_INDEX.byAlias.get(name);
    if (hit) return hit;
  }
  return null;
};

// Expand a single essential-oil ingredient row into N synthetic constituent
// rows. The synthetic rows mirror the shape of `performFuzzySearchAndCheck`
// output so they slot into the existing pipeline without code changes.
export const unfoldIngredient = (ingredient) => {
  const resolved = resolveComposition(ingredient);
  if (!resolved) return null;
  const { entry, key: oilKey } = resolved;
  const oilPercent = Number(ingredient.formula_percentage);
  if (!Number.isFinite(oilPercent) || oilPercent <= 0) return null;
  return entry.composition.map((constituent) => {
    const fraction = Number(constituent.percentTypical) / 100;
    const effectivePercent = oilPercent * fraction;
    return {
      // Provenance — these fields let the UI show "from <oil>" on each row.
      derivedFrom: {
        oil: oilKey,
        oilName:
          ingredient.ingredient_name ||
          ingredient.MATERIAL ||
          oilKey.replace(/\.pdf$/, ""),
        oilPercentage: oilPercent,
        constituentPercent: constituent.percentTypical,
        source: entry.source,
        sourceUrl: entry.sourceUrl,
      },
      ingredient_name: constituent.name,
      cas_number: constituent.casNumber,
      formula_percentage: Number(effectivePercent.toFixed(6)),
      concentration_verified: true,
      validIngredient: true,
      isComposition: true,
    };
  });
};

// Unfold every oil in a formula and return both the original rows and the
// synthetic constituent rows interleaved by parent oil. Allergen aggregation
// happens by the consumer because the existing fuzzy-search + IFRA-threshold
// pipeline already aggregates by CAS during the limit checks.
export const unfoldFormula = (ifraResults = []) => {
  const out = [];
  ifraResults.forEach((row) => {
    out.push(row);
    const expanded = unfoldIngredient(row);
    if (expanded) out.push(...expanded);
  });
  return out;
};

// Sum every constituent contribution by CAS across the entire (unfolded)
// formula. Returns `Map<CAS, { total, contributors: [{ oil, percent }, …] }>`
// — the basis for true SOTA allergen aggregation (e.g. three oils each
// carrying 15% linalool sum to one total-linalool figure rather than three
// independent below-threshold readings).
export const aggregateConstituents = (unfoldedResults = []) => {
  const totals = new Map();
  unfoldedResults.forEach((row) => {
    if (!row.isComposition) return;
    const cas = normaliseCas(row.cas_number);
    if (!cas) return;
    const current = totals.get(cas) || {
      casNumber: cas,
      name: row.ingredient_name,
      total: 0,
      contributors: [],
    };
    current.total += Number(row.formula_percentage) || 0;
    current.contributors.push({
      oil: row.derivedFrom.oilName,
      percent: row.formula_percentage,
    });
    totals.set(cas, current);
  });
  return totals;
};

// Tiny helper for tests — list every oil the platform recognises.
export const listKnownOils = () =>
  Object.keys(compositions).filter((k) => !k.startsWith("_"));
